import axios from "axios";

export default {
  state: {
    serre: [],
    snapshots: {},
    file_stagione: {}, // key: `${id_serra}_${id_stagione}` → FilaStagione[]
  },
  getters: {
    getSerre(state) {
      return state.serre;
    },
    getSerraSnapshot(state) {
      return (id_serra, id_stagione) =>
        state.snapshots[`${id_serra}_${id_stagione}`] || [];
    },
    isSnapshotCached(state) {
      return (id_serra, id_stagione) =>
        `${id_serra}_${id_stagione}` in state.snapshots;
    },
    getFileStagione(state) {
      return (id_serra, id_stagione) =>
        state.file_stagione[`${id_serra}_${id_stagione}`] || [];
    },
    isFileStagioneCached(state) {
      return (id_serra, id_stagione) =>
        `${id_serra}_${id_stagione}` in state.file_stagione;
    },
  },
  mutations: {
    setSerre(state, serre) {
      state.serre = serre;
    },
    addSerra(state, serra) {
      state.serre.push(serra);
    },
    updateSerra(state, serra) {
      state.serre = state.serre.map((s) => (s.id === serra.id ? serra : s));
    },
    removeSerra(state, id) {
      state.serre = state.serre.filter((s) => s.id !== id);
    },
    setSerraSnapshot(state, { id_serra, id_stagione, posizioni }) {
      const key = `${id_serra}_${id_stagione}`;
      state.snapshots = { ...state.snapshots, [key]: posizioni };
    },
    addPosizioneSerra(state, posizione) {
      const key = `${posizione.id_serra}_${posizione.id_stagione}`;
      const lista = state.snapshots[key] || [];
      state.snapshots = { ...state.snapshots, [key]: [...lista, posizione] };
    },
    updatePosizioneSerra(state, posizione) {
      const key = `${posizione.id_serra}_${posizione.id_stagione}`;
      const lista = state.snapshots[key] || [];
      state.snapshots = {
        ...state.snapshots,
        [key]: lista.map((p) => (p.id === posizione.id ? posizione : p)),
      };
    },
    removePosizioneSerra(state, { id, id_serra, id_stagione }) {
      const key = `${id_serra}_${id_stagione}`;
      const lista = state.snapshots[key] || [];
      state.snapshots = {
        ...state.snapshots,
        [key]: lista.filter((p) => p.id !== id),
      };
    },
    setFileStagione(state, { id_serra, id_stagione, file }) {
      const key = `${id_serra}_${id_stagione}`;
      state.file_stagione = { ...state.file_stagione, [key]: file };
    },
    // Svuota la cache di file_stagione per una coppia (usato dopo salvataggio)
    invalidateFileStagione(state, { id_serra, id_stagione }) {
      const key = `${id_serra}_${id_stagione}`;
      const copia = { ...state.file_stagione };
      delete copia[key];
      state.file_stagione = copia;
    },
  },
  actions: {
    fetchSerre({ commit }) {
      const url = process.env.VUE_APP_API_ROOT + "/admin/serre";
      const auth = this.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .get(url, { auth })
          .then((response) => {
            commit("setSerre", response.data);
            resolve(response.data);
          })
          .catch((error) => {
            console.error("Errore nel recupero delle serre:", error);
            reject(error);
          })
      );
    },
    saveSerra(context, payload) {
      const url = process.env.VUE_APP_API_ROOT + "/admin/serre";
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .post(url, payload, { auth })
          .then((response) => {
            context.commit("addSerra", response.data);
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },
    updateSerra(context, { id, payload }) {
      const url = process.env.VUE_APP_API_ROOT + "/admin/serre/" + id;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .put(url, payload, { auth })
          .then((response) => {
            context.commit("updateSerra", response.data);
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },
    deleteSerra(context, id) {
      const url = process.env.VUE_APP_API_ROOT + "/admin/serre/" + id;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .delete(url, { auth })
          .then((response) => {
            context.commit("removeSerra", id);
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },

    // ---- Snapshot piazzamenti ----

    fetchSerraSnapshot(context, { id_serra, id_stagione }) {
      const key = `${id_serra}_${id_stagione}`;
      // presenza della chiave, non truthiness: uno snapshot legittimamente
      // vuoto ([]) è comunque cache valida (stessa semantica di isSnapshotCached)
      if (key in context.state.snapshots) {
        return Promise.resolve(context.state.snapshots[key]);
      }
      const url =
        process.env.VUE_APP_API_ROOT +
        "/admin/serre/" + id_serra +
        "/snapshot/" + id_stagione;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .get(url, { auth })
          .then((response) => {
            context.commit("setSerraSnapshot", {
              id_serra,
              id_stagione,
              posizioni: response.data,
            });
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },
    fetchSnapshotsAppezzamento(context, { id_stagione, id_appezzamento, id_serre = [] }) {
      const url = `${process.env.VUE_APP_API_ROOT}/admin/stagione/${id_stagione}/appezzamento/${id_appezzamento}/snapshots`;
      const auth = context.getters.get_credential;
      return axios.get(url, { auth }).then((response) => {
        const ricevute = new Set(Object.keys(response.data));
        for (const [id_serra, posizioni] of Object.entries(response.data)) {
          context.commit('setSerraSnapshot', { id_serra, id_stagione, posizioni });
        }
        // Il bulk è scopato sull'Appezzamento LIVE: una serra presente solo nel
        // fork stagionale (o la cui posizione live è stata rimossa) non compare
        // nella risposta. Assumerla vuota azzererebbe colori, completamento e
        // recap di una serra che in realtà ha piazzamenti: caricala singolarmente.
        for (const id_serra of id_serre) {
          if (!ricevute.has(String(id_serra))) {
            context.dispatch('fetchSerraSnapshot', { id_serra, id_stagione });
          }
        }
        return response.data;
      });
    },
    savePosizioneSerra(context, { id_serra, id_stagione, payload }) {
      const url =
        process.env.VUE_APP_API_ROOT +
        "/admin/serre/" + id_serra +
        "/snapshot/" + id_stagione;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .post(url, payload, { auth })
          .then((response) => {
            context.commit("addPosizioneSerra", response.data);
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },
    updatePosizioneSerra(context, { id_posizione, payload }) {
      const url =
        process.env.VUE_APP_API_ROOT + "/admin/serre/posizione/" + id_posizione;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .put(url, payload, { auth })
          .then((response) => {
            context.commit("updatePosizioneSerra", response.data);
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },
    deletePosizioneSerra(context, { id_posizione, id_serra, id_stagione }) {
      const url =
        process.env.VUE_APP_API_ROOT + "/admin/serre/posizione/" + id_posizione;
      const auth = context.getters.get_credential;
      return new Promise((resolve, reject) =>
        axios
          .delete(url, { auth })
          .then((response) => {
            context.commit("removePosizioneSerra", {
              id: id_posizione,
              id_serra,
              id_stagione,
            });
            resolve(response.data);
          })
          .catch((error) => reject(error))
      );
    },

    // ---- FilaStagione ----

    fetchFileStagione(context, { id_serra, id_stagione }) {
      const key = `${id_serra}_${id_stagione}`;
      if (key in context.state.file_stagione) {
        return Promise.resolve(context.state.file_stagione[key]);
      }
      const url = `${process.env.VUE_APP_API_ROOT}/admin/serre/${id_serra}/file_stagione/${id_stagione}`;
      const auth = context.getters.get_credential;
      return axios.get(url, { auth }).then((r) => {
        context.commit('setFileStagione', { id_serra, id_stagione, file: r.data });
        return r.data;
      });
    },

    // Riconciliazione completa: invia l'array completo, il backend crea/aggiorna/elimina
    saveFileStagione(context, { id_serra, id_stagione, file }) {
      const url = `${process.env.VUE_APP_API_ROOT}/admin/serre/${id_serra}/file_stagione/${id_stagione}`;
      const auth = context.getters.get_credential;
      return axios.put(url, { file }, { auth }).then((r) => {
        context.commit('setFileStagione', { id_serra, id_stagione, file: r.data });
        return r.data;
      });
    },
  },
};
